import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Calendar, Repeat, ChevronRight } from 'lucide-react';
import { track } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHEDULE_TYPES = [
  { id: 'school', label: 'School Run', hint: 'Weekday morning + afternoon' },
  { id: 'office', label: 'Weekly Office Commute', hint: 'Same route, set days' },
  { id: 'airport', label: 'Recurring Airport', hint: 'Weekly / monthly Pearson runs' },
  { id: 'custom', label: 'Something Else', hint: 'Tell us in your own words' },
];

const DAYS = [
  { id: 'mon', label: 'M' },
  { id: 'tue', label: 'T' },
  { id: 'wed', label: 'W' },
  { id: 'thu', label: 'T' },
  { id: 'fri', label: 'F' },
  { id: 'sat', label: 'S' },
  { id: 'sun', label: 'S' },
];

const DAY_FULL = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

export const RecurringScheduleModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [scheduleType, setScheduleType] = useState('');
  const [days, setDays] = useState([]);
  const [pickupTime, setPickupTime] = useState('');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [error, setError] = useState('');
  const dialogRef = useRef(null);

  // Close on Escape; lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => dialogRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Reset state when modal closes (after animation)
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(1); setScheduleType(''); setDays([]); setPickupTime('');
        setPickup(''); setDropoff(''); setName(''); setEmail(''); setPhone('');
        setNotes(''); setSubmitted(false); setBookingId(''); setError('');
      }, 350);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const toggleDay = (id) => {
    setDays((prev) => prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]);
  };

  const selectSchedule = (id) => {
    setScheduleType(id);
    // Pre-fill common patterns
    if (id === 'school') {
      setDays(['mon', 'tue', 'wed', 'thu', 'fri']);
      setPickupTime('07:45');
    } else if (id === 'office') {
      setDays(['mon', 'tue', 'wed', 'thu', 'fri']);
    } else if (id === 'airport') {
      setDays(['sun']);
    }
    setStep(2);
  };

  const canSubmit = name.trim() && email.trim() && phone.trim() && pickup.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');

    const typeLabel = SCHEDULE_TYPES.find((t) => t.id === scheduleType)?.label || 'Recurring schedule';
    const daysText = days.length ? days.map((d) => DAY_FULL[d]).join(', ') : 'To be confirmed';
    const recurringSummary = [
      '— RECURRING FAMILY SCHEDULE REQUEST —',
      `Schedule type: ${typeLabel}`,
      `Days: ${daysText}`,
      pickupTime ? `Preferred pickup time: ${pickupTime}` : '',
      notes ? `\nClient notes: ${notes}` : '',
    ].filter(Boolean).join('\n');

    // Use a tomorrow start date so the booking lands in the future
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isoStart = `${tomorrow.toISOString().split('T')[0]}T${pickupTime || '09:00'}`;

    try {
      const res = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          pickupLocation: pickup.trim(),
          dropoffLocation: dropoff.trim() || 'To be confirmed',
          dateTime: isoStart,
          notes: recurringSummary,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        track('booking_submit_success', {
          flow: 'recurring_schedule',
          schedule_type: typeLabel,
          days_count: days.length,
          booking_id: data.booking_id,
        });
        setBookingId(data.booking_id);
        setSubmitted(true);
      } else {
        track('booking_submit_error', { flow: 'recurring_schedule', status: res.status });
        setError('Something went wrong. Please call +1 (437) 996-3801 and we will set it up by phone.');
      }
    } catch {
      track('booking_submit_error', { flow: 'recurring_schedule', status: 'network' });
      setError('Unable to submit right now. Please call +1 (437) 996-3801 and we will set it up by phone.');
    }
    setSubmitting(false);
  };

  return (
    <div
      data-testid="recurring-modal-backdrop"
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        backgroundColor: 'rgba(5,5,5,0.78)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        animation: 'fadeIn 260ms ease-out',
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="recurring-modal-title"
        data-testid="recurring-modal"
        onClick={(e) => e.stopPropagation()}
        className="recurring-modal-card"
        style={{
          width: '100%',
          maxWidth: '620px',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          backgroundColor: 'rgba(14,14,14,0.96)',
          border: '1px solid rgba(194,168,120,0.22)',
          boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(194,168,120,0.05)',
          position: 'relative',
          animation: 'fadeInUp 380ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {/* Gold accent line top */}
        <div style={{
          position: 'absolute', top: 0, left: '12%', right: '12%', height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.55), transparent)',
        }} />

        {/* Close */}
        <button
          data-testid="recurring-modal-close"
          onClick={onClose}
          aria-label="Close recurring schedule dialog"
          style={{
            position: 'absolute', top: '18px', right: '18px',
            width: '36px', height: '36px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'transparent',
            border: '1px solid rgba(194,168,120,0.15)',
            color: 'rgba(220,220,220,0.7)',
            cursor: 'pointer', transition: 'all 200ms ease', zIndex: 2,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(194,168,120,0.5)'; e.currentTarget.style.color = '#C2A878'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(194,168,120,0.15)'; e.currentTarget.style.color = 'rgba(220,220,220,0.7)'; }}
        >
          <X size={16} />
        </button>

        {submitted ? (
          <div data-testid="recurring-modal-success" style={{ padding: '64px 40px 56px', textAlign: 'center' }}>
            <div style={{
              width: '60px', height: '60px', margin: '0 auto 28px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(194,168,120,0.08)', border: '1px solid rgba(194,168,120,0.3)',
              borderRadius: '50%',
            }}>
              <Check size={24} style={{ color: '#C2A878' }} />
            </div>
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.7)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '14px' }}>
              Request #{bookingId}
            </p>
            <h3 className="font-serif" style={{ fontSize: '28px', fontWeight: 500, color: '#F5F5F5', marginBottom: '16px', lineHeight: 1.25 }}>
              We&apos;ve got your schedule.
            </h3>
            <p style={{ fontSize: '14px', color: 'rgba(220,220,220,0.7)', lineHeight: 1.8, fontWeight: 300, maxWidth: '380px', margin: '0 auto 32px' }}>
              The founder will personally call you within one business hour to confirm chauffeur assignment, lock in your rate, and set up the recurring schedule.
            </p>
            <button
              data-testid="recurring-modal-success-close"
              onClick={onClose}
              style={{
                backgroundColor: 'transparent', color: '#C2A878', padding: '12px 28px',
                fontSize: '12px', fontWeight: 400, letterSpacing: '1px', textTransform: 'uppercase',
                border: '1px solid rgba(194,168,120,0.3)', cursor: 'pointer',
                transition: 'all 300ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.06)'; e.currentTarget.style.borderColor = 'rgba(194,168,120,0.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(194,168,120,0.3)'; }}
            >
              Close
            </button>
          </div>
        ) : (
          <div style={{ padding: '40px clamp(24px, 5vw, 48px) 36px' }}>
            {/* Header */}
            <div style={{ marginBottom: '28px', paddingRight: '40px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <Repeat size={13} style={{ color: '#C2A878' }} strokeWidth={1.6} />
                <span style={{ fontSize: '10px', color: 'rgba(194,168,120,0.75)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 400 }}>
                  Recurring Family Schedule
                </span>
              </div>
              <h2 id="recurring-modal-title" className="font-serif" style={{ fontSize: 'clamp(24px, 3.2vw, 30px)', fontWeight: 500, color: '#F5F5F5', lineHeight: 1.2, marginBottom: '10px' }}>
                Tell us your week — we&apos;ll match the same chauffeur.
              </h2>
              <p style={{ fontSize: '13px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.65 }}>
                School runs, office commutes, weekly airport — a single dedicated chauffeur, a locked monthly rate, and zero surge pricing.
              </p>
            </div>

            {/* Step 1 — Schedule type chooser */}
            {step === 1 && (
              <div data-testid="recurring-step-1">
                <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '16px' }}>
                  01 · What kind of schedule?
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {SCHEDULE_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`recurring-type-${t.id}`}
                      onClick={() => selectSchedule(t.id)}
                      style={{
                        textAlign: 'left',
                        padding: '18px 22px',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(194,168,120,0.18)',
                        color: '#F5F5F5',
                        cursor: 'pointer',
                        transition: 'border-color 220ms ease, background-color 220ms ease, padding-left 220ms ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '12px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(194,168,120,0.55)';
                        e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.05)';
                        e.currentTarget.style.paddingLeft = '28px';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(194,168,120,0.18)';
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)';
                        e.currentTarget.style.paddingLeft = '22px';
                      }}
                    >
                      <div>
                        <div className="font-serif" style={{ fontSize: '17px', fontWeight: 400, marginBottom: '4px' }}>{t.label}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(220,220,220,0.5)', fontWeight: 300 }}>{t.hint}</div>
                      </div>
                      <ChevronRight size={16} style={{ color: 'rgba(194,168,120,0.55)', flexShrink: 0 }} strokeWidth={1.4} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2 — Details */}
            {step === 2 && (
              <form onSubmit={handleSubmit} data-testid="recurring-step-2">
                <button
                  type="button"
                  data-testid="recurring-back-to-step-1"
                  onClick={() => setStep(1)}
                  style={{
                    background: 'transparent', border: 'none', color: 'rgba(194,168,120,0.7)',
                    fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase',
                    padding: 0, cursor: 'pointer', marginBottom: '18px',
                  }}
                >
                  ← Change schedule type
                </button>

                <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '12px' }}>
                  02 · Which days &amp; what time?
                </p>

                {/* Days of week pills */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  {DAYS.map((d, i) => {
                    const active = days.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        data-testid={`recurring-day-${d.id}`}
                        onClick={() => toggleDay(d.id)}
                        aria-pressed={active}
                        aria-label={DAY_FULL[d.id]}
                        style={{
                          width: '42px', height: '42px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: active ? '#C2A878' : 'rgba(255,255,255,0.03)',
                          color: active ? '#0B0B0B' : 'rgba(225,225,225,0.7)',
                          border: active ? '1px solid #C2A878' : '1px solid rgba(194,168,120,0.18)',
                          fontSize: '13px', fontWeight: active ? 600 : 400,
                          cursor: 'pointer', transition: 'all 200ms ease',
                          borderRadius: '2px',
                        }}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>

                {/* Pickup time */}
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="rec-time" style={labelSt}>Preferred Pickup Time</label>
                  <input
                    id="rec-time"
                    data-testid="recurring-pickup-time"
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="rec-field"
                  />
                </div>

                <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '14px' }}>
                  03 · Route &amp; contact
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="recurring-grid">
                  <div>
                    <label htmlFor="rec-pickup" style={labelSt}>Pickup *</label>
                    <input
                      id="rec-pickup"
                      data-testid="recurring-pickup"
                      type="text"
                      placeholder="e.g. Oakville home"
                      value={pickup}
                      onChange={(e) => setPickup(e.target.value)}
                      required
                      className="rec-field"
                    />
                  </div>
                  <div>
                    <label htmlFor="rec-dropoff" style={labelSt}>Drop-off</label>
                    <input
                      id="rec-dropoff"
                      data-testid="recurring-dropoff"
                      type="text"
                      placeholder="e.g. Appleby College"
                      value={dropoff}
                      onChange={(e) => setDropoff(e.target.value)}
                      className="rec-field"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }} className="recurring-grid">
                  <div>
                    <label htmlFor="rec-name" style={labelSt}>Your Name *</label>
                    <input
                      id="rec-name"
                      data-testid="recurring-name"
                      type="text"
                      placeholder="Full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="rec-field"
                    />
                  </div>
                  <div>
                    <label htmlFor="rec-phone" style={labelSt}>Phone *</label>
                    <input
                      id="rec-phone"
                      data-testid="recurring-phone"
                      type="tel"
                      placeholder="(416) 555-0100"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="rec-field"
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label htmlFor="rec-email" style={labelSt}>Email *</label>
                  <input
                    id="rec-email"
                    data-testid="recurring-email"
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rec-field"
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="rec-notes" style={labelSt}>
                    Anything we should know? <span style={{ color: 'rgba(220,220,220,0.4)', textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <textarea
                    id="rec-notes"
                    data-testid="recurring-notes"
                    rows={3}
                    placeholder="Car seat, school dismissal time, the chauffeur your child already knows…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="rec-field"
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {error && (
                  <p data-testid="recurring-modal-error" style={{
                    fontSize: '13px', color: '#E07A7A', marginBottom: '16px', fontWeight: 300,
                  }}>{error}</p>
                )}

                {/* Sticky footer — keeps submit button visible while scrolling */}
                <div style={{
                  position: 'sticky',
                  bottom: 0,
                  marginLeft: 'calc(-1 * clamp(24px, 5vw, 48px))',
                  marginRight: 'calc(-1 * clamp(24px, 5vw, 48px))',
                  marginTop: '8px',
                  padding: 'clamp(16px, 3vw, 22px) clamp(24px, 5vw, 48px) clamp(20px, 3vw, 28px)',
                  background: 'linear-gradient(180deg, rgba(14,14,14,0) 0%, rgba(14,14,14,0.92) 30%, rgba(14,14,14,0.98) 100%)',
                  borderTop: '1px solid rgba(194,168,120,0.08)',
                }}>
                  <button
                    type="submit"
                    data-testid="recurring-submit"
                    disabled={submitting || !canSubmit}
                    style={{
                      width: '100%',
                      backgroundColor: canSubmit ? '#C2A878' : 'rgba(194,168,120,0.25)',
                      color: canSubmit ? '#0B0B0B' : 'rgba(11,11,11,0.5)',
                      padding: '15px 24px',
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '1.4px',
                      textTransform: 'uppercase',
                      border: 'none',
                      cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
                      transition: 'all 300ms ease',
                      boxShadow: canSubmit ? '0 4px 24px rgba(194,168,120,0.2)' : 'none',
                    }}
                  >
                    {submitting ? 'Sending…' : 'Reserve My Schedule'}
                  </button>

                  <p style={{ fontSize: '11px', color: 'rgba(220,220,220,0.45)', fontWeight: 300, textAlign: 'center', marginTop: '12px', lineHeight: 1.55 }}>
                    <Calendar size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: '5px', color: 'rgba(194,168,120,0.5)' }} strokeWidth={1.4} />
                    No charge today. The founder will call you within one business hour to confirm.
                  </p>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const labelSt = {
  display: 'block',
  fontSize: '10px',
  color: 'rgba(194,168,120,0.7)',
  letterSpacing: '1.8px',
  textTransform: 'uppercase',
  fontWeight: 400,
  marginBottom: '8px',
};
